<template>
  <v-container>
    <v-row no-gutters class="my-5">
      <v-col cols="12" md="1">
        <router-link to="/advisors" class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" lg="7" offset-lg="2" xl="5" offset-xl="3">
        <v-card v-if="showActivationLink" class="activation-link-card">
          <v-card-title> Activation link </v-card-title>
          <v-card-text>
            <p>
              Please copy this invite link and paste it into an email that you
              send to the user.
            </p>
            <p>
              Activation link for {{ previousAddedUser.fullName }} ({{
                previousAddedUser.email
              }}): <br />
              <a :href="previousAddedUser.activationLink">{{
                previousAddedUser.activationLink
              }}</a>
            </p>
          </v-card-text>
        </v-card>

        <h1>{{ addUserTitle }}</h1>
        <ValidationObserver ref="observer">
          <v-form @submit.prevent="submit">
            <v-row>
              <v-col cols="6" class="pb-0">
                <ValidationProvider
                  id="first_names"
                  v-slot="{ errors }"
                  name="first_names"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="user.firstNames"
                    label="First names*"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="6" class="pb-0">
                <ValidationProvider
                  id="last_name"
                  v-slot="{ errors }"
                  name="last_name"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="user.lastName"
                    label="Last name*"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <ValidationProvider
              v-if="whitelabel.authentication.signUp.showEmail"
              id="email"
              v-slot="{ errors }"
              name="email"
              rules="required|email"
            >
              <v-text-field
                v-model.trim="user.email"
                label="Email*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="whitelabel.authentication.signUp.showUniqueIdentifier"
              id="unique_identifier"
              v-slot="{ errors }"
              name="uniqueIdentifier"
              rules="required"
            >
              <v-text-field
                id="unique_identifier_input"
                v-model.trim="user.uniqueIdentifier"
                :error-messages="errors"
                label="Industry Number*"
              />
            </ValidationProvider>
            <ValidationProvider
              v-if="whitelabel.authentication.signUp.showPhone"
              id="phone"
              v-slot="{ errors }"
              name="phone"
              rules="required|phone"
            >
              <v-text-field
                id="phone_input"
                v-model.trim="user.phone"
                :error-messages="errors"
                label="Cell Number*"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="isAdmin || isCompanyAdmin"
              id="role"
              v-slot="{ errors }"
              name="role"
              rules="required"
            >
              <v-select
                v-model="user.role"
                :items="roles"
                label="Role*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="selectedUserIsAdvisor"
              id="advisor_code"
              v-slot="{ errors }"
              name="advisor_code"
              rules="adviserCode"
            >
              <v-text-field
                v-model.trim="user.advisorCode"
                label="Advisor code"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="selectedUserRole"
              id="external_reference"
              v-slot="{ errors }"
              name="external_reference"
              rules="required"
            >
              <v-text-field
                v-model.trim="user.externalReference"
                label="External reference*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="isLeadProvider"
              id="lead_provider_code"
              v-slot="{ errors }"
              name="manager email address"
              rules="required|email"
            >
              <v-text-field
                v-model.trim="user.managerEmail"
                label="Manager email address*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="showSppLeadProvider && isAdmin"
              id="spp_lead_provider"
              v-slot="{ errors }"
              name="spp_lead_provider"
            >
              <label class="text-subtitle-1"
                >Should this user have access to create DFA SPP leads?</label
              >
              <v-checkbox
                v-model="user.sppProvider"
                label="SPP Lead Provider"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="isCallCenterAgentOrConsultant"
              id="system_wide_access"
              v-slot="{ errors }"
              name="system_wide_access"
              rules="required"
            >
              <label class="text-subtitle-1"
                >Should this user have access to all companies or only the
                company assigned below?*</label
              >
              <v-radio-group
                v-model="user.systemWideAccess"
                row
                class="mt-2"
                :error-messages="errors"
              >
                <v-radio
                  label="System Wide Access"
                  :value="true"
                  color="primary"
                />
                <v-radio
                  label="Company Specific Access"
                  :value="false"
                  color="primary"
                />
              </v-radio-group>
            </ValidationProvider>

            <ValidationProvider
              v-if="isAdmin && selectedUserIsAdvisor"
              id="company_wide_access"
              v-slot="{ errors }"
              name="company_wide_access"
              rules="required"
            >
              <label class="text-subtitle-1"
                >Should this user have access to all testators within their
                company or only the testators assigned to them?*</label
              >
              <v-radio-group
                v-model="user.companyWideAccess"
                row
                class="mt-2"
                :error-messages="errors"
              >
                <v-radio
                  label="Company Wide Access"
                  :value="true"
                  color="primary"
                />
                <v-radio
                  label="Default Access"
                  :value="false"
                  color="primary"
                />
              </v-radio-group>
            </ValidationProvider>

            <ValidationProvider
              v-if="showCompaniesSelect"
              id="company"
              v-slot="{ errors }"
              name="company"
              rules="required"
            >
              <v-select
                v-model="user.companyId"
                :items="companyOptions"
                item-value="id"
                item-text="tradingName"
                label="Company*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="showBranchesSelect"
              id="branch"
              v-slot="{ errors }"
              name="branch"
              rules="required"
            >
              <v-select
                v-model="user.branchId"
                :items="branchOptions"
                item-value="id"
                item-text="description"
                label="Branch*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="showAppointmentLink"
              id="appointment_link"
              v-slot="{ errors }"
              name="appointment_link"
            >
              <v-text-field
                id="appointment_link_input"
                v-model.trim="user.appointmentLink"
                :error-messages="errors"
                label="Appointment Link"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="showWillCountries"
              id="will_country"
              v-slot="{ errors }"
              name="will_country"
              rules="required"
            >
              <v-select
                v-model="willCountry"
                :items="$options.willCountries"
                label="Which country would you like the will to be created in?*"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              id="send_email"
              v-slot="{ errors }"
              name="send_email"
              rules="required"
            >
              <label class="text-subtitle-1"
                >Let {{ whitelabel.name }} send the invite
                {{
                  whitelabel.authentication.signUp.showEmail ? 'email' : 'SMS'
                }}
                or you can copy the invite link and send it yourself in an
                {{
                  whitelabel.authentication.signUp.showEmail ? 'email' : 'SMS'
                }}?*</label
              >
              <v-radio-group
                v-model="sendEmail"
                row
                class="mt-2"
                :error-messages="errors"
              >
                <v-radio
                  :label="
                    whitelabel.authentication.invite.method == 'sms'
                      ? 'Send SMS invite'
                      : 'Send email invite'
                  "
                  :value="true"
                  color="primary"
                />
                <v-radio
                  label="Get invite link"
                  :value="false"
                  color="primary"
                />
              </v-radio-group>
            </ValidationProvider>

            <v-select
              v-if="isAdmin && user.role != 'TESTATOR' && user.role"
              v-model="commissionScheme"
              :items="commissionSchemeOptions"
              item-text="text"
              item-value="value"
              label="Commission Scheme (override)"
              hint="Leave blank to inherit the company's scheme."
              persistent-hint
              clearable
              class="mb-4"
            />
            <object-commission-structure
              v-if="isAdmin && user.role != 'TESTATOR' && user.role"
              object_type="User"
              :initial-commission-structures="user.objectCommissionPairs || []"
              :show-inheritance="true"
              :user-role="user.role"
              @commission-data-updated="handleCommissionDataUpdate"
              @selected-commission-structure="handleSelectedCommissionStructure"
              @commission-structure-code="handleCommissionStructureCode"
            />

            <v-btn
              class="submit-button"
              :loading="buttonLoading"
              rounded
              depressed
              large
              type="submit"
              color="primary"
            >
              <span>{{ addUserTitle }}</span
              ><v-icon small>{{ icons.mdiArrowRight }}</v-icon>
            </v-btn>
          </v-form>
        </ValidationObserver>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </v-container>
</template>

<script>
  import { CREATE_USER_MUTATION } from '../../constants/graphql/users';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { SIMPLE_COMPANIES_QUERY } from '../../constants/graphql/companies';
  import { commissionSchemeOptions } from '../../constants/js/commission-schemes';
  import { COMPANY_BRANCHES_QUERY } from '../../constants/graphql/branches';
  import ObjectCommissionStructure from './commission/ObjectCommissionStructure.vue';

  export default {
    name: 'AddUser',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
      ObjectCommissionStructure,
    },
    mixins: [formHelperMixin],
    data() {
      return {
        user: {},
        sendEmail: null,
        willCountry: null,
        previousAddedUser: {},
        showActivationLink: false,
        showSuccess: false,
        successMessage: null,
        errorMessage: null,
        companies: [],
        branches: [],
        commissionData: {
          objectCommissionPairs: [],
          commissionStructure: 'NONE',
        },
        commissionStructureCode: null,
        commissionScheme: null,
        commissionSchemeOptions,
      };
    },
    willCountries: [
      { text: 'South Africa', value: 'ZA' },
      { text: 'United Kingdom', value: 'GB' },
    ],
    computed: {
      selectedUserRole() {
        return (
          this.user.role == 'CALL_CENTER_AGENT' || this.user.role == 'ADVISOR'
        );
      },
      selectedUserIsAdvisor() {
        return this.user.role == 'ADVISOR';
      },
      isCallCenterAgentOrConsultant() {
        return (
          this.user.role == 'CALL_CENTER_AGENT' ||
          this.user.role == 'FIDUCIARY_CONSULTANT'
        );
      },
      isLeadProvider() {
        return this.user.role == 'LEAD_PROVIDER';
      },
      showSppLeadProvider() {
        return ['ADMIN', 'CALL_CENTER_AGENT', 'ADVISOR'].includes(
          this.user.role
        );
      },
      showCompaniesSelect() {
        return (
          [
            'ADVISOR',
            'COMPANY_ADMIN',
            'FIDUCIARY_CONSULTANT',
            'CALL_CENTER_AGENT',
            'BRANCH_AGENT',
            'LEAD_PROVIDER',
          ].includes(this.user.role) && this.isAdmin
        );
      },
      showBranchesSelect() {
        return this.branches.length > 0 && this.user.role === 'BRANCH_AGENT';
      },
      showAppointmentLink() {
        return (
          this.user.role === 'ADVISOR' &&
          this.companyOptions.find(
            company => company.id === this.user.companyId
          )?.advisorAppointment
        );
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      isCompanyAdmin() {
        return this.$store.getters.isCompanyAdmin;
      },
      isCallCenterAgent() {
        return this.$store.getters.isCallCenterAgent;
      },
      isBranchAgent() {
        return this.$store.getters.isBranchAgent;
      },
      isAdvisor() {
        return this.$store.getters.isAdvisor;
      },
      loggedInUserCompanyId() {
        return this.$store.getters.companyId;
      },
      addUserTitle() {
        if (this.isAdvisor) {
          return 'Add new testator';
        } else {
          return 'Add new user';
        }
      },
      roles() {
        if (this.isCompanyAdmin) {
          return [
            { text: 'Testator', value: 'TESTATOR' },
            { text: 'Advisor', value: 'ADVISOR' },
          ];
        } else if (this.isAdmin) {
          return [
            { text: 'Testator', value: 'TESTATOR' },
            { text: 'Advisor', value: 'ADVISOR' },
            { text: 'Fiduciary consultant', value: 'FIDUCIARY_CONSULTANT' },
            { text: 'Call center agent', value: 'CALL_CENTER_AGENT' },
            { text: 'Branch agent', value: 'BRANCH_AGENT' },
            { text: 'Company admin', value: 'COMPANY_ADMIN' },
            { text: 'Admin', value: 'ADMIN' },
            { text: 'Lead provider', value: 'LEAD_PROVIDER' },
          ];
        } else {
          return [];
        }
      },
      showWillCountries() {
        return process.env.VUE_APP_INCLUDE_GB_WILL;
      },
      companyOptions() {
        if (this.user.role == 'FIDUCIARY_CONSULTANT') {
          return this.companies.filter(c => c.companyType == 'AUTO_ASSIGN');
        } else {
          return this.companies;
        }
      },
      branchOptions() {
        return this.branches;
      },
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.createUser();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createUser() {
        if (this.isAdvisor || this.isCallCenterAgent || this.isBranchAgent) {
          this.user.role = 'TESTATOR';
        }
        if (this.isCallCenterAgent || this.isBranchAgent) {
          this.user.companyId = this.loggedInUserCompanyId;
        }

        const userInputDetails = {
          ...this.user,
          objectCommissionPairsAttributes:
            this.commissionData.objectCommissionPairs,
          commissionStructure: this.user.commissionStructure,
          commissionStructureCode: this.commissionStructureCode,
          commissionScheme: this.commissionScheme,
        };
        const sendEmail = this.sendEmail;
        const willCountry = this.showWillCountries ? this.willCountry : 'ZA';

        this.$apollo
          .mutate({
            mutation: CREATE_USER_MUTATION,
            variables: {
              userInputDetails,
              sendEmail,
              willCountry,
            },
          })
          .then(response => {
            this.setPreviousAddedUser(response.data.createUser);
            this.showSuccessMessage();
          })
          .catch(error => {
            this.errorMessage = error.message
              .replace('GraphQL error:', '')
              .trim();
            this.displayError();
          });
      },
      setPreviousAddedUser(user) {
        this.showActivationLink = !this.sendEmail;
        this.previousAddedUser = user;
      },
      showSuccessMessage() {
        this.user = {};
        this.$refs.observer.reset();
        this.buttonLoading = false;
        this.successMessage = this.sendEmail
          ? `User added and invited by ${
              this.whitelabel.authentication.invite.method == 'sms'
                ? 'SMS'
                : 'email'
            }`
          : 'User added. See activation link below.';
        this.showSuccess = true;
        this.sendEmail = null;
        this.willCountry = null;
      },
      async fetchBranches(companyId) {
        try {
          const response = await this.$apollo.query({
            query: COMPANY_BRANCHES_QUERY,
            variables: {
              id: companyId,
            },
            fetchPolicy: 'network-only',
          });

          this.branches = response.data.companyBranches;
        } catch (error) {
          console.error('Error fetching company branches:', error);
        }
      },
      handleCommissionDataUpdate(data) {
        this.commissionData = data;
      },
      handleSelectedCommissionStructure(value) {
        this.user.commissionStructure = value;
      },
      handleCommissionStructureCode(code) {
        this.commissionStructureCode = code;
      },
    },
    watch: {
      'user.companyId': async function (newValue) {
        if (newValue && this.user.role === 'BRANCH_AGENT') {
          await this.fetchBranches(newValue);
        }
      },
      'user.role': async function (newValue) {
        if (newValue === 'BRANCH_AGENT' && this.user.companyId) {
          await this.fetchBranches(this.user.companyId);
        }
      },
    },
    apollo: {
      companies: {
        query: SIMPLE_COMPANIES_QUERY,
        skip() {
          return !this.isAdmin;
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .submit-button {
    float: left;
    margin-top: 30px;
  }

  .activation-link-card {
    margin-bottom: 20px;

    p {
      color: #000000;
    }
  }
</style>
